import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { imageUrl, formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, Package, CheckCircle2, AlertTriangle, XCircle, ArrowLeft, Mail } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
        setActiveImg(0);
        document.title = `${data.name} · Mymi Global Business`;
      } catch { setNotFound(true); }
    })();
  }, [id]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", { ...form, product_id: product.id, product_name: product.name });
      setSent(true);
      toast.success("Request sent — we'll reply shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send.");
    }
    setSending(false);
  };

  if (notFound) return <div className="max-w-3xl mx-auto py-20 px-4 text-center text-slate-600">Product not found. <Link to="/inventory" className="text-blue-600">Back to inventory</Link>.</div>;
  if (!product) return <div className="max-w-3xl mx-auto py-20 px-4 text-center text-slate-500">Loading…</div>;

  const specs = product.specs || {};
  const specEntries = Object.entries(specs).filter(([, v]) => v);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/inventory" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900" data-testid="back-to-inventory">
        <ArrowLeft className="w-4 h-4" /> Back to inventory
      </Link>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {product.images?.[activeImg] ? (
              <img src={imageUrl(product.images[activeImg])} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-slate-300">No image</div>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${activeImg === i ? "border-slate-900" : "border-slate-200"}`}
                >
                  <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">{product.category}</span>
            <span className="text-xs text-slate-400">·</span>
            <span className="text-xs font-medium text-slate-500">{product.brand}{product.model ? ` · ${product.model}` : ""}</span>
          </div>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{product.name}</h1>

          <div className="mt-5 flex items-center gap-4 flex-wrap">
            <div className="font-display text-3xl font-bold text-slate-900">${product.price.toFixed(2)}</div>
            <StatusLabel status={product.status} qty={product.stock_quantity} />
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200">
              Condition: {product.condition}
            </span>
          </div>

          <p className="mt-5 text-slate-700 leading-relaxed">{product.description}</p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">SKU</div>
              <div className="font-mono text-sm text-slate-900 mt-0.5">{product.sku}</div>
            </div>
            {product.warranty && (
              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Warranty</div>
                <div className="text-sm text-slate-900 mt-0.5">{product.warranty}</div>
              </div>
            )}
          </div>

          <a href="#request" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white font-semibold text-sm hover:bg-blue-600 transition-colors" data-testid="scroll-to-request">
            <Mail className="w-4 h-4" /> Request to Purchase
          </a>
        </div>
      </div>

      {/* Specs */}
      {specEntries.length > 0 && (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-bold mb-4">Specifications</h2>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <dl className="divide-y divide-slate-200">
              {specEntries.map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 px-5 py-3.5 text-sm" data-testid={`spec-${k.toLowerCase().replace(/\s/g, "-")}`}>
                  <dt className="text-slate-500 font-medium">{k}</dt>
                  <dd className="col-span-2 text-slate-900">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {product.included_accessories?.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-2xl font-bold mb-4">Included</h2>
          <ul className="flex flex-wrap gap-2">
            {product.included_accessories.map((a, i) => (
              <li key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700">
                <Package className="w-3.5 h-3.5 text-slate-400" /> {a}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Request form */}
      <section id="request" className="mt-16 rounded-3xl bg-slate-900 text-white p-8 sm:p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Request to Purchase</h2>
            <p className="mt-3 text-slate-300 leading-relaxed">Send us a message and we'll confirm availability, invoice details, and delivery or pickup options.</p>
            <div className="mt-6 text-sm text-slate-400 font-mono">SKU · {product.sku}</div>
          </div>
          <form onSubmit={submitInquiry} className="space-y-3" data-testid="product-inquiry-form">
            {sent ? (
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-6 text-emerald-200" data-testid="inquiry-success">
                Thanks — your request was sent. We'll be in touch shortly.
              </div>
            ) : (
              <>
                <input required data-testid="inquiry-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500" />
                <input required type="email" data-testid="inquiry-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500" />
                <input data-testid="inquiry-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500" />
                <textarea required data-testid="inquiry-message" rows={4} placeholder="Your message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500" />
                <button disabled={sending} data-testid="inquiry-submit" className="w-full px-5 py-3 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold transition-colors">
                  {sending ? "Sending…" : "Send request"}
                </button>
              </>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}

function StatusLabel({ status, qty }) {
  if (status === "available") return <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700"><CheckCircle2 className="w-4 h-4" /> In stock ({qty} available)</span>;
  if (status === "low_stock") return <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700"><AlertTriangle className="w-4 h-4" /> Low stock ({qty} left)</span>;
  if (status === "out_of_stock") return <span className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-700"><XCircle className="w-4 h-4" /> Out of stock</span>;
  return <span className="text-sm font-medium text-slate-500">{status}</span>;
}
